import { getEnv, Instance, types as t } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import toCss from 'to-css'
import { v4 as uuid } from 'uuid'
import Auditable from './auditable'
import clone from '../../utils/mst-clone'
import defaultSections from '../data/defaultThemeContent'

const Style = t
  .model('ThemeStyle', {
    id: t.identifier,
    description: t.maybeNull(t.string),
    cssClass: t.maybeNull(t.string),
    cssValue: t.maybeNull(t.string),
    cssProperty: t.maybeNull(t.string)
  })
  .views(self => ({
    get previewCss () {
      try {
        return toCss({ [self.cssClass]: { [self.cssProperty]: self.cssValue } }, {
          selector: (selector, declaration) => {
            return '.layout-editor-wrapper ' + selector
          }
        })
      } catch (e) {}
    },
    get css () {
      try {
        return toCss({ [self.cssClass]: { [self.cssProperty]: self.cssValue } })
      } catch (e) {}
    },
    get isError () {
      return !(self as any).previewCss?.length
    }
  }))
  .preProcessSnapshot(snapshot => {
    if (!snapshot.id) {
      snapshot.id = uuid()
    }
    return snapshot
  })
  .actions(crudActions)

const Section = t
  .model('ThemeSection', {
    label: t.maybeNull(t.string),
    styles: t.optional(t.array(Style), [])
  })
  .actions(crudActions)
  .actions(self => {
    function newStyle () {
      self.styles.push({ id: uuid() })
    }
    function cloneStyle (item) {
      self.styles.push({ ...item, id: uuid() })
    }
    function deleteStyle (item) {
      self.styles.splice(self.styles.indexOf(item), 1)
    }
    return {
      newStyle,
      cloneStyle,
      deleteStyle
    }
  })

export const Theme = t
  .model('Theme', {
    id: t.identifier,
    name: t.string,
    sections: t.optional(t.array(Section), defaultSections)
  })
  .views(self => ({
    getSnapshotWithNewIds () {
      return clone(self, () => uuid())
    },
    get previewCss () {
      const css = []
      self.sections.forEach(sect => {
        sect.styles.forEach(s => {
          css.push(s.previewCss)
        })
      })
      return css.join('\n')
    },
    get css () {
      const env = getEnv(self)
      if (env?.css?.[self.id]) return env.css[self.id]
      const css = []
      self.sections.forEach(sect => {
        sect.styles.forEach(s => {
          css.push(s.css)
        })
      })
      return css.join('\n')
    }
  }))
  .actions(crudActions)
  .actions(self => {
    function update (key, val) {
      self[key] = val
    }
    function updateItem (item) {
      Object.keys(item).forEach(k => {
        self[k] = item[k]
      })
    }
    function newSection (item) {
      self.sections.push(item)
    }
    function deleteSection (item) {
      self.sections.splice(self.sections.indexOf(item), 1)
    }
    return {
      update,
      updateItem,
      newSection,
      deleteSection
    }
  })

export interface ITheme extends Instance<typeof Theme> {}
export interface IStyle extends Instance<typeof Style> {}
export interface ISection extends Instance<typeof Section> {}

export default t.compose(
  'Theme',
  Theme,
  Auditable
)
